import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  TextField, Snackbar, Alert, CircularProgress
} from '@mui/material';
import TitleBar from '../../components/TitleBar/TitleBar';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';
import {
  getAllReviewDimensions,
  createReviewDimension,
  updateReviewDimension,
  deleteReviewDimension,
  ReviewDimension
} from '../../services/reviewDimensionService';

const ReviewDimensionSetup: React.FC = () => {
  const navigate = useNavigate();
  const [dimensions, setDimensions] = useState<ReviewDimension[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<string>('');
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<'edit' | 'add'>('edit');
  const [currentDimension, setCurrentDimension] = useState<ReviewDimension>({
    id: 0,
    name: '',
    description: ''
  });
  const [originalName, setOriginalName] = useState<string>('');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info' as 'success' | 'error' | 'info' | 'warning',
  });

  useEffect(() => {
    fetchDimensions();
  }, []);

  const fetchDimensions = async () => {
    setLoading(true);
    try {
      const data = await getAllReviewDimensions();
      setDimensions(data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch review dimensions');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (name: string) => {
    setDeleteTarget(name);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTarget('');
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteReviewDimension(deleteTarget);
      setSnackbar({
        open: true,
        message: `Review dimension "${deleteTarget}" deleted successfully`,
        severity: 'success'
      });
      fetchDimensions();
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Failed to delete review dimension: ${(err as Error).message}`,
        severity: 'error'
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };

  const handleOpenEditDialog = (dimension: ReviewDimension) => {
    setEditMode('edit');
    setCurrentDimension(dimension);
    setOriginalName(dimension.name);
    setOpenEditDialog(true);
  };

  const handleOpenAddDialog = () => {
    setEditMode('add');
    setCurrentDimension({ id: 0, name: '', description: '' });
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => setOpenEditDialog(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentDimension(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitDimension = async () => {
    try {
      if (editMode === 'add') {
        await createReviewDimension({
          name: currentDimension.name,
          description: currentDimension.description
        });
        setSnackbar({
          open: true,
          message: `Review dimension "${currentDimension.name}" created successfully`,
          severity: 'success'
        });
      } else {
        await updateReviewDimension({
          name: currentDimension.name,
          description: currentDimension.description
        });
        setSnackbar({
          open: true,
          message: `Review dimension "${currentDimension.name}" updated successfully`,
          severity: 'success'
        });
      }
      fetchDimensions();
      handleCloseEditDialog();
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Unknown error occurred';
      setSnackbar({
        open: true,
        message: `Failed to ${editMode === 'add' ? 'create' : 'update'} review dimension: ${errorMessage}`,
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const isFormValid = () =>
    currentDimension.name.trim() !== '' && currentDimension.description.trim() !== '';

  return (
    <Box sx={{ flexGrow: 1 }}>
      <TitleBar title="" />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header Row with Back IconButton + Title + Add Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              aria-label="Back"
              onClick={() => navigate('/organization-setup')}
              sx={{
                border: '1px solid #ccc',
                borderRadius: 2,
                p: 1,
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 2 }}>
              Review Dimension Setup
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Review Dimension
          </Button>
        </Box>

        <Paper sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : dimensions.length === 0 ? (
            <Typography variant="body1" align="center">
              No review dimensions found. Click the button above to add one.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><b>Name</b></TableCell>
                    <TableCell><b>Description</b></TableCell>
                    <TableCell align="center"><b>Actions</b></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dimensions.map((dimension) => (
                    <TableRow key={dimension.name}>
                      <TableCell>{dimension.name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {dimension.description?.split(' ').slice(0, 6).join(' ') +
                            (dimension.description?.split(' ').length > 6 ? '...' : '')}
                          {dimension.description?.split(' ').length > 6 && (
                            <InfoIcon sx={{ ml: 0.5, fontSize: '0.8rem', color: 'text.secondary' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton size="small" color="primary" onClick={() => handleOpenEditDialog(dimension)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleOpenDeleteDialog(dimension.name)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the review dimension "{deleteTarget}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editMode === 'add' ? 'Add' : 'Edit'} Review Dimension</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            name="name"
            fullWidth
            value={currentDimension.name}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={3}
            value={currentDimension.description}
            onChange={handleInputChange}
          />
        </DialogContent>
        <DialogActions>
  <Button onClick={handleCloseEditDialog}>Cancel</Button>
  <Button
    variant="contained"
    color="primary"
    onClick={handleSubmitDimension}
    disabled={!isFormValid()}
    sx={{ textTransform: 'none' }}
  >
    {editMode === 'add' ? 'Create' : 'Update'}
  </Button>
</DialogActions>

      </Dialog>

      {/* Snackbar for Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReviewDimensionSetup;
